import { useMemo, useState } from 'react'
import { css } from '@emotion/react'
import { colors, isIndexResultValid } from 'shared-ui'
import PageSubtitle from '../_general/PageSubtitle'
import { FormattedMessage, useIntl } from 'react-intl'
import FancyDropdown from '../_control/FancyDropdown'
import { useCallback } from 'react'
import useRawState from '../../hooks/useRawState'
import FallbackMessage from '../_general/FallbackMessage'
import LoaderText from '../_general/LoaderText'
import PresentationModal from '../_layout/PresentationModal'
import RemountByInterval from '../_general/RemountByInterval'
import PerformancePitchesTable from '../index/PerformancePitchesTable'
import Modal from '../_layout/Modal'
import BasicMeasurementPicker from '../_control/BasicMeasurementPicker'
import Cta from '../_control/Cta'
import KioskImage from '../../assets/icons/kiosk-screen.svg'
import { ReactSVG } from 'react-svg'
import LocalStorage from '../../services/LocalStorage'
import { useEffect } from 'react'
import VisibilityToggle from '../_control/VisibilityToggle'

export default function ClubPitchListPerformance({
    club,
    isBusy,
    isEmptyState,
    venuePitches,
    sortPitches,
    PageTitleWithLogo,
}) {
    const { formatMessage } = useIntl()

    const { data: indexCategories, isBusy: loadingIndexCategories } = useRawState(
        '/api/current/index-categories',
        { pagination: false }
    )

    const [selectedTrainingGoal, setSelectedTrainingGoal] = useState(null)

    const trainingGoalOptions = useMemo(() => {  // this needs to be an indexMetricType dropdown
        if (!club || loadingIndexCategories || !indexCategories) return []

        const sport = club?.sport || 'football'
        const filtered = indexCategories.filter(cat => cat.indexCategoryGroup === 'training-goal' && cat.sport === sport)
            .toSorted((a, b) => {
                const norm = v => Number.isFinite(v) ? v : Number.isFinite(+v) ? +v : Infinity;
                const srA = norm(a.sortRank)
                const srB = norm(b.sortRank)
                return srA - srB
            })

        return [
            ...filtered.map(cat => {
                return {
                    value: cat.id,
                    label: cat.name,
                }
            })
        ]
    }, [indexCategories, loadingIndexCategories, club])

    const [venueIndexes, setVenueIndexes] = useState({})

    //  to help generate this state from child components
    const handleAddIndexSortListData = useCallback((venueId, pitchId, indexCategoryId, indexData) => {
        setVenueIndexes(prev => {

            // only add data if not already present
            const existing = prev[venueId]?.find(ind => ind.id === indexData.id)
            if (existing) return prev

            const copy = { ...prev }
            if (!copy[venueId]) copy[venueId] = []
            copy[venueId].push({
                ...indexData,
                indexCategoryId,
                pitchId,
            })
            return copy
        })
    }, [])

    const getSortedPitchIds = useCallback((venueId) => {

        const indexes = venueIndexes[venueId] ?? []
        const filtered = indexes.filter(ind => ind.indexCategoryId === selectedTrainingGoal)  // keeps only the index/traininggoal we are interested in sorting on

        const sorted = filtered.toSorted((a, b) => (b.value ?? 0) - (a.value ?? 0))  // order descending

        const valid = []
        const expired = []

        sorted.forEach(ind => {
            const isValid = isIndexResultValid(ind, new Date())
            if (isValid) {
                valid.push(ind.pitchId)
            } else {
                expired.push(ind.pitchId)
            }
        })

        return { valid, expired }
    }, [venueIndexes, selectedTrainingGoal])

    const selectedIndexLabel = indexCategories?.find(cat => cat.id === selectedTrainingGoal)?.name

    // make sure the state persists when auth refreshes jwt (causing a remount of the entire app component tree)
    const PRESENTING_OPTIONS = LocalStorage.getItem('presentation-mode-options')

    const [showPresentationMode, setShowPresentationMode] = useState(PRESENTING_OPTIONS?.ready ?? false)
    const [presentationOptions, setPresentationOptions] = useState(PRESENTING_OPTIONS ?? {})

    useEffect(() => {
        // persist whether we are currently in a presentation so the session can be resumed after auth refreshes
        if (presentationOptions.ready) LocalStorage.setItem('presentation-mode-options', presentationOptions)
    }, [presentationOptions])

    const controlPresentation = (action, options) => {
        if (action === 'stop') {
            setPresentationOptions(prev => ({ ...prev, ready: false }))
            setShowPresentationMode(false)
            LocalStorage.removeItem('presentation-mode-options')
        } else if (action === 'start') {
            setPresentationOptions(prev => ({ ...prev, ready: true }))
            const bod = document.body
            if (bod.requestFullscreen) bod.requestFullscreen()
        } else if (action === 'setup') {
            setPresentationOptions(options)
        }
    }

    const [expiredVisible, setExpiredVisible] = useState(LocalStorage.getItem('showExpiredIndices') ?? true)
    useEffect(() => {
        LocalStorage.setItem('showExpiredIndices', expiredVisible)
    }, [expiredVisible])

    return <>
        <div css={style.pageHeader}>

            {PageTitleWithLogo}

            <div css={style.headerControlsWrapper}>
                <VisibilityToggle
                    isVisible={expiredVisible}
                    setIsVisible={setExpiredVisible}
                    title={formatMessage({ id: 'toggleVisibilityOfOldData' })}
                />
                <div
                    onClick={() => setShowPresentationMode(true)}
                    css={style.presentationButton}
                    title={formatMessage({ id: 'presentationMode' })}
                >
                    <ReactSVG src={KioskImage} />
                </div>
                < label css={style.dropdownWrapper} >
                    <div css={style.dropdownLabelText}>
                        {formatMessage({ id: 'trainingGoal' })}
                    </div>
                    <FancyDropdown
                        options={trainingGoalOptions}
                        isClearable={true}
                        placeholder={formatMessage({ id: 'select' })}
                        value={trainingGoalOptions.find(o => o.value === selectedTrainingGoal) ?? null}
                        onChange={opt => setSelectedTrainingGoal(opt?.value || null)}
                        css={style.dropdown}
                    />
                </ label>
            </div>
        </div>

        {isBusy ? <div>
            <PageSubtitle>
                <LoaderText />
            </PageSubtitle>

        </div>
            : isEmptyState ? <FallbackMessage>
                <FormattedMessage id='noPitchesWarn' />
            </FallbackMessage>
                :
                <div>
                    {venuePitches.map(({ venueName, venue, pitches }, index) => {
                        const { valid: sortedPI, expired } = getSortedPitchIds(venue?.id)
                        const pitchList = sortPitches(pitches, { showPerformance: true, selectedTrainingGoal, sortedPI, expiredPI: expired })

                        return !presentationOptions.ready ?  // to avoid this component lazy fetching when in presentation mode
                            <PerformancePitchesTable
                                key={venueName ?? index}
                                venue={venue}
                                venueIndexes={venueIndexes}
                                pitches={pitchList}
                                selectedTrainingGoal={selectedTrainingGoal}
                                selectedIndexLabel={selectedIndexLabel}
                                upliftIndexData={handleAddIndexSortListData}
                                indexCategories={indexCategories}
                                headerStyle={[style.tableHeader, css`color: ${colors.soft};`]}
                                expiredVisible={expiredVisible}
                            />
                            :
                            null
                    })}
                </div>
        }
        {showPresentationMode &&
            <>
                {!presentationOptions.ready ?
                    <PresentationOptionsForm
                        presentationOptions={presentationOptions}
                        controlPresentation={controlPresentation}
                        venuePitches={venuePitches}
                    />
                    :
                    <PresentationModal
                        onClickClose={() => controlPresentation('stop')}
                        speed={presentationOptions.speed}
                        css={css`background: ${colors.dark};`}
                    >
                        <RemountByInterval durationMinutes={10} clearData={() => setVenueIndexes({})} >
                            <div css={css`
                                padding: 0 1em; 
                                max-width: 110em;
                                width: 100%;
                            `}>
                                {venuePitches.map(({ venueName, venue, pitches }, index) => {
                                    const { valid: sortedPI, expired } = getSortedPitchIds(venue?.id)
                                    const pitchList = sortPitches(pitches, { showPerformance: true, selectedTrainingGoal, sortedPI, expiredPI: expired })
                                    // filter the pitches from options
                                    const filteredPitches = pitchList.filter(p => presentationOptions.pitchInclusions.includes(p.id))
                                    if (filteredPitches.length === 0) return

                                    return <PerformancePitchesTable
                                        key={'presentation' + (venueName ?? index)}
                                        venue={venue}
                                        venueIndexes={venueIndexes}
                                        pitches={filteredPitches}
                                        selectedTrainingGoal={selectedTrainingGoal}
                                        selectedIndexLabel={selectedIndexLabel}
                                        upliftIndexData={handleAddIndexSortListData}
                                        indexCategories={indexCategories}
                                        stickyHeader={true}
                                        headerStyle={[style.tableHeader, css`color: white; font-size: 1.3em;`]}
                                        expiredVisible={expiredVisible}
                                    />
                                })}
                            </div>
                        </RemountByInterval>
                    </PresentationModal>
                }
            </>
        }
    </>
}

function PresentationOptionsForm({ venuePitches, presentationOptions, controlPresentation }) {

    const { formatMessage } = useIntl()

    const initState = presentationOptions.pitchInclusions || venuePitches.map(({ pitches }) => {
        return [...pitches.map(p => p.id)]
    }).flat()

    const [pitchInclusions, setPitchInclusions] = useState(initState)
    const [speed, setSpeed] = useState(presentationOptions.speed || 2)

    const applySettingsAndStart = () => {
        controlPresentation('setup', { speed, pitchInclusions })
        controlPresentation('start')
    }

    const toggleVenue = (pitches) => {
        const pitchesIds = pitches.map(p => p.id)

        if (allIn(pitchesIds, pitchInclusions)) {
            setPitchInclusions(
                removeAll(pitchesIds, pitchInclusions)
            )
        } else {
            setPitchInclusions(
                addMissing(pitchesIds, pitchInclusions)
            )
        }
    }

    const togglePitch = (id) => {
        setPitchInclusions(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id])
    }

    return <Modal
        header={formatMessage({ id: 'setUpPresentationMode' })}
        onClickClose={() => controlPresentation('stop')}
        css={style.optionsModal}
    >
        <div css={css`display: grid; gap: 1em;`}>
            <h2 css={css`font-size: 1em; color: ${colors.main1};`}>
                {`${formatMessage({ id: 'sportsFields' })}  (${pitchInclusions.length})`}
            </h2>
            {venuePitches?.map(({ venueName, venue, pitches }) => <div >
                <PageSubtitle
                    key={venue}
                    css={[
                        style.venue,
                        noneIn(pitches.map(p => p.id), pitchInclusions) ?
                            css`background: transparent; color: ${colors.soft};`
                            : null
                    ]}
                    onClick={() => toggleVenue(pitches)}
                >
                    {venueName}
                </PageSubtitle>

                <div css={style.pitchesGrid}>
                    {pitches.map(pitch => {
                        const isIncluded = pitchInclusions.includes(pitch.id)
                        return <div
                            key={pitch.id}
                            onClick={() => { togglePitch(pitch.id) }}
                            css={style.pitchBtn(isIncluded)}>
                            {pitch.name}
                        </div>
                    })}
                </div>
            </div>)}

            <h2 css={css`font-size: 1em; color: ${colors.main1}; margin-top: 1em;`}>
                {formatMessage({ id: 'scrollSpeed' })}
            </h2>
            <BasicMeasurementPicker
                selected={speed}
                values={[1, 2, 3, 4]}
                onChange={(e) => setSpeed(e)}
                css={css`width: min-content;`}
            />
        </div>

        <div css={style.formSubmit}>
            <Cta onClick={applySettingsAndStart}>
                {formatMessage({ id: 'confirm' })}
            </Cta>
        </div>
    </Modal>
}

function allIn(arr1, arr2) {
    const set2 = new Set(arr2)
    return arr1.every(el => set2.has(el))
}

function noneIn(arr1, arr2) {
    const set2 = new Set(arr2)
    return arr1.every(el => !set2.has(el))
}

const removeAll = (a, b) => {
    const aSet = new Set(a)
    return b.filter(x => !aSet.has(x))
}

const addMissing = (a, b) => {
    const bSet = new Set(b)
    return [...b, ...a.filter(x => !bSet.has(x))]
}

const style = {
    pageHeader: css`
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-between;
    `,
    dropdown: css`
        min-width: 14em;
        color: white;
        @media screen and (max-width: 600px) {
            margin - bottom: 1em;
        }
    `,
    dropdownWrapper: css`
        display: flex;
        align-items: center;
        gap: 1em;
        color: ${colors.soft};
    `,
    dropdownLabelText: css`
        @media screen and (max-width: 600px) {
            display: none;
        }
    `,
    tableHeader: css`
        padding: 0.5em;
        opacity: 1;
        background: ${colors.dark};
        vertical-align: text-bottom !important;
        padding-top: 0;
    `,
    venue: css`
        color: ${colors.white};
        cursor: pointer;
        background: ${colors.eventLight};
        width: min-content;
        white-space: nowrap;
        padding: 0.2em;
        border-radius: 5px;
        border: 2px solid ${colors.eventDark};
        margin-bottom: 0.25em;
        margin-top: 0.5em;
        font-size: 1.6em;

        &:hover {
            background: ${colors.eventDark};
            color: ${colors.soft};
        }
    `,
    headerControlsWrapper: css`
        display: flex;
        gap: 2em;
        align-items: center;
    `,
    presentationButton: css`
        padding: 0.25em; 
        color: ${colors.soft};
        width: 2.5em;
        height: 2.5em;

        &:hover {
            background: ${colors.eventLight};
            border-radius: 5px;
            cursor: pointer;
            color: white;
        }
    `,
    optionsModal: css`
        user-select: none;
        width: min(60em, 80vw);
    `,
    pitchesGrid: css`
        display: grid;
        grid-template-columns: 1fr 1fr 1fr;
        gap: 0.5em 1.5em;

        @media screen and (max-width: 600px) {
            grid-template-columns: 1fr 1fr;
        }
    `,
    pitchBtn: (isIncluded) => css`
        background: ${isIncluded ? colors.eventLight : 'transparent'};
        padding: 0.5em;
        border: 2px solid ${colors.eventDark};
        border-radius: 5px;
        color: ${isIncluded ? 'white' : colors.soft};
        cursor: pointer;
        opacity: ${isIncluded ? 1 : 0.5};

        &:hover {
            color: ${colors.soft};
        }
    `,
    formSubmit: css`
        display: flex;
        justify-content: end;
        align-items: center;
    `
}